import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Grid, useMediaQuery, useTheme, Divider } from '@mui/material';
import Paper from '@mui/material/Paper';
import { CreateTaxImage, ZeroResultsImage } from '@/resources/images';
import {
    FilterButton,
    TableCard,
    TableMaker,
    TableMobileLabel,
    PageEmptyState,
    Pagination,
    PageTitle,
    SearchInput,
    SEO,
    SortButton,
    ListingClearButton,
    WrapWithPaper,
    TableSkeleton
} from '@/components/common';
import { TaxFilterDrawer } from '@/components/FilterDrawers';
import { taxDiscountSortMenuItems } from '@/config';
import {
    useCheckHasBusinessLoaded,
    useLocale,
    useListingState,
    useUrlParams
} from '@/hooks';
import { getPaginatedTaxes } from '@/modules/dataWrangler/dataWrangler';
import { TAX_BASE_PARAM } from '@/constants';
import { openDrawer, fetchListingItems } from '@/state/actions';
import { Container } from '@/containers';
import { TaxesActive } from '@/resources/icons';
import tableHead from './tableHead';
import { generateComponents, dataProcessor } from './dataProcessors';
import useStyles from '../styles';

const Taxes = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const locale = useLocale();
    const hasBusinessLoaded = useCheckHasBusinessLoaded();
    const { setUrlParams, getUrlParams } = useUrlParams();

    const {
        hasData,
        items,
        loadingItems,
        loadingPaper,
        totalPages,
        zeroResultsReturned
    } = useListingState('tax');

    const classes = useStyles({ hasData });
    const dispatch = useDispatch();
    const currentUrlParams = getUrlParams({ base: TAX_BASE_PARAM });
    const stringifiedParams = JSON.stringify(currentUrlParams);

    const setOrder = (property) => {
        const sortOrder =
            currentUrlParams?.sort?.type === property &&
            currentUrlParams?.sort?.order === 'asc'
                ? 'desc'
                : 'asc';

        const newUrlParams = {
            ...currentUrlParams,
            sort: {
                type: property,
                order: sortOrder
            }
        };

        setUrlParams({ base: TAX_BASE_PARAM, params: newUrlParams });
    };

    useEffect(() => {
        if (hasBusinessLoaded) {
            getPaginatedTaxes(JSON.parse(stringifiedParams));
        }
    }, [hasBusinessLoaded, stringifiedParams]);

    return (
        <>
            <SEO title="All Taxes" />
            <Container>
                {!loadingItems && !hasData && (
                    <PageEmptyState
                        image={<CreateTaxImage />}
                        content="Create Your First Tax Item"
                        buttonText="Create Tax"
                        onClick={() => {
                            dispatch(openDrawer('tax'));
                        }}
                    />
                )}
                {hasData && !loadingPaper && (
                    <>
                        {!isMobile && (
                            <Paper elevation={0} variant="outlined">
                                <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    alignItems="center"
                                >
                                    <Grid item md={2}>
                                        <PageTitle
                                            icon={<TaxesActive />}
                                            title="Taxes"
                                        />
                                    </Grid>
                                    <Grid item md={4}>
                                        <div className={classes.searchWrap}>
                                            <SearchInput
                                                base={TAX_BASE_PARAM}
                                                id="dropdownSearch"
                                                label="Search"
                                                ariaLabel="Search"
                                            />
                                        </div>
                                    </Grid>

                                    <Grid
                                        item
                                        md={2}
                                        className={classes.filterButtonWrap}
                                    >
                                        <Grid container alignItems="center">
                                            <Grid item md={8}>
                                                <FilterButton
                                                    base={TAX_BASE_PARAM}
                                                    type="tax"
                                                    watch={[
                                                        'value',
                                                        'type',
                                                        'tagIds'
                                                    ]}
                                                />
                                            </Grid>
                                            <Grid item md={4}>
                                                <ListingClearButton
                                                    base={TAX_BASE_PARAM}
                                                    watch={[
                                                        's',
                                                        'value',
                                                        'type',
                                                        'tagIds'
                                                    ]}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        item
                                        container
                                        justifyContent="flex-end"
                                        md={4}
                                        className={classes.newButtonContainer}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={() =>
                                                dispatch(openDrawer('tax'))
                                            }
                                            className={classes.newButton}
                                        >
                                            New Tax
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Divider />

                                <TableMaker
                                    actionCols={[3, 4, 5]}
                                    ariaTableName="Taxes Table"
                                    handleRequestSort={setOrder}
                                    headCells={tableHead}
                                    rowData={
                                        hasData && !zeroResultsReturned
                                            ? generateComponents({
                                                  array: dataProcessor(items),
                                                  locale
                                              })
                                            : []
                                    }
                                    order={
                                        currentUrlParams?.sort?.order || 'asc'
                                    }
                                    orderBy={currentUrlParams?.sort?.type || ''}
                                />
                                {zeroResultsReturned && (
                                    <PageEmptyState
                                        image={<ZeroResultsImage />}
                                        content="The selected filters returned 0 results."
                                        buttonText="Reset filters"
                                        onClick={() => {
                                            dispatch(
                                                fetchListingItems({
                                                    type: 'tax'
                                                })
                                            );
                                            setUrlParams({
                                                base: TAX_BASE_PARAM,
                                                params: null
                                            });
                                        }}
                                        hidePaper
                                    />
                                )}
                                <Pagination
                                    base={TAX_BASE_PARAM}
                                    totalPages={totalPages}
                                />
                            </Paper>
                        )}

                        {isMobile && (
                            <>
                                <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    alignItems="center"
                                >
                                    <Grid item xs={6}>
                                        <PageTitle
                                            icon={<TaxesActive />}
                                            title="Taxes"
                                        />
                                    </Grid>

                                    <Grid
                                        item
                                        container
                                        justifyContent="flex-end"
                                        xs={6}
                                        className={classes.newButtonContainer}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            className={classes.newButton}
                                        >
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                onClick={() =>
                                                    dispatch(openDrawer('tax'))
                                                }
                                            >
                                                New Tax
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <div className={classes.searchWrap}>
                                            <SearchInput
                                                base={TAX_BASE_PARAM}
                                                id="dropdownSearch"
                                                label="Search"
                                                ariaLabel="Search"
                                            />
                                        </div>
                                    </Grid>
                                </Grid>
                                <Grid container justifyContent="center">
                                    <Grid
                                        item
                                        container
                                        spacing={1}
                                        xs={12}
                                        direction="row"
                                        wrap="nowrap"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Grid
                                            item
                                            container
                                            xs={6}
                                            className={classes.sortButtonWrap}
                                        >
                                            <SortButton
                                                base={TAX_BASE_PARAM}
                                                isLoading={loadingItems}
                                                sortMenuItems={
                                                    taxDiscountSortMenuItems
                                                }
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={6}
                                            className={classes.filterButtonWrap}
                                        >
                                            <FilterButton
                                                base={TAX_BASE_PARAM}
                                                type="tax"
                                                watch={[
                                                    'value',
                                                    'type',
                                                    'tagIds'
                                                ]}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item md={4}>
                                        <ListingClearButton
                                            base={TAX_BASE_PARAM}
                                            watch={[
                                                's',
                                                'value',
                                                'type',
                                                'sort',
                                                'tagIds'
                                            ]}
                                            hide={
                                                zeroResultsReturned ||
                                                loadingItems
                                            }
                                        />
                                    </Grid>
                                </Grid>
                                {hasData &&
                                    !zeroResultsReturned &&
                                    generateComponents({
                                        array: dataProcessor(items),
                                        locale,
                                        isMobile
                                    }).map((item) => (
                                        <TableCard
                                            key={item.id}
                                            title={item.taxName}
                                            onClick={item.primaryClick}
                                            left={
                                                <>
                                                    <TableMobileLabel label="Amount" />
                                                    <span>{item.amount}</span>
                                                </>
                                            }
                                            actions={item.actions}
                                            tags={item?.tags}
                                        />
                                    ))}
                                {zeroResultsReturned && (
                                    <PageEmptyState
                                        image={<ZeroResultsImage />}
                                        content="The selected filters returned 0 results."
                                        buttonText="Reset filters"
                                        onClick={() => {
                                            dispatch(
                                                fetchListingItems({
                                                    type: 'tax'
                                                })
                                            );
                                            setUrlParams({
                                                base: TAX_BASE_PARAM,
                                                params: null
                                            });
                                        }}
                                    />
                                )}
                                {totalPages > 1 && (
                                    <Paper elevation={0} variant="outlined">
                                        <Pagination
                                            base={TAX_BASE_PARAM}
                                            totalPages={totalPages}
                                        />
                                    </Paper>
                                )}
                            </>
                        )}
                    </>
                )}
                {loadingPaper && (
                    <WrapWithPaper removeWrap={isMobile}>
                        <TableSkeleton isMobile={isMobile} />
                    </WrapWithPaper>
                )}
                {!loadingPaper && <TaxFilterDrawer />}
            </Container>
        </>
    );
};

export default Taxes;
